/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import copyTextToClipboard from 'ggApp/utils/copyToClipboard';

import Copy from 'ggApp/shared/icons/Copy';
import WhatsAppIcon from 'ggApp/shared/icons/Whatsapp';
import FacebookIcon from 'ggApp/shared/icons/Facebook';
import FacebookMessangerIcon from 'ggApp/shared/icons/FacebookMessanger';
import TelegramIcon from 'ggApp/shared/icons/Telegram';
import TwitterIcon from 'ggApp/shared/icons/Twitter';
import SmsIcon from 'ggApp/shared/icons/Sms';
import LinkedInIcon from 'ggApp/shared/icons/LinkedIn';
import loadConfiguration from 'ggApp/utils/configuration/configuration';
import Link from 'ggApp/shared/icons/Link';
import { Translator } from 'ggApp/modules/translator/components';
import ShareButton from './ShareButton';

import { Container, Title, List, Item, DefaultItem, CopyItem } from './styles';

const { FACEBOOK_ID } = loadConfiguration();

const LinkList = [
    {
        icon: <WhatsAppIcon />,
        text: 'WhatsApp',
        getLink: (url, text) => {
            const query = qs.stringify({
                utm_source: 'grover',
                utm_medium: 'whatsapp',
                text: `${text} ${url}`,
            });
            return `https://api.whatsapp.com/send?${query}`;
        },
    },
    {
        icon: <FacebookIcon />,
        text: 'Facebook',
        getLink: (url, text) => {
            const query = qs.stringify({
                u: url,
                quote: text,
            });
            return `https://www.facebook.com/sharer/sharer.php?${query}`;
        },
    },
    {
        icon: <FacebookMessangerIcon />,
        text: 'Facebook Messanger',
        getLink: (url) => {
            const query = qs.stringify({
                link: url,
                app_id: FACEBOOK_ID,
            });
            return `fb-messenger://share/?${query}`;
        },
        onlyForMobile: true,
    },
    {
        icon: <TelegramIcon />,
        text: 'Telegram',
        getLink: (url, text) => {
            const query = qs.stringify({
                url,
                text,
            });
            return `tg://msg_url?${query}`;
        },
    },
    {
        icon: <TwitterIcon />,
        text: 'Twitter',
        getLink: (url, text) => {
            const query = qs.stringify({
                url,
                text,
            });
            return `https://twitter.com/intent/tweet?${query}`;
        },
    },
    {
        icon: <SmsIcon />,
        text: 'SMS Message',
        getLink: (url, text) => {
            const query = qs.stringify({
                body: `${text} ${url}`,
            });
            return `sms:?&${query}`;
        },
    },
    {
        icon: <LinkedInIcon />,
        text: 'LinkedIn',
        getLink: (url, text) => {
            const query = qs.stringify({
                mini: true,
                url,
                title: text,
            });
            return `https://www.linkedin.com/shareArticle?${query}`;
        },
    },
];

const SocialSharingList = ({
    className,
    link,
    message,
    isMobile,
    needsTitle,
    needsDefaultCopy,
    onClickItem,
    needsCopyItem,
    registerReferralSharingEvent,
    page,
    isReferralModal,
    onCopy,
    copyCtaText,
}) => {
    const [isActive, setIsActive] = React.useState(false);

    const handleClick = useCallback(
        (networkName, eventAction) => () => {
            onClickItem(networkName);
            // eslint-disable-next-line no-unused-expressions
            isReferralModal &&
                registerReferralSharingEvent({
                    eventLabel: networkName,
                    eventAction,
                });
        },
        [onClickItem, isReferralModal, registerReferralSharingEvent],
    );

    const handleCopy = () => {
        setIsActive(true);
        setTimeout(() => setIsActive(false), 2000);
        copyTextToClipboard({ text: link });
        registerReferralSharingEvent({
            eventLabel: 'ref_link_copied',
            eventAction: 'invite',
        });
        if (onCopy) {
            onCopy(copyCtaText);
        }
    };

    return (
        <Container className={className}>
            {needsTitle && (
                <Title>
                    <Translator tk="INVITE_LINK_SHARING_HEADLINE" />
                </Title>
            )}
            <List>
                {needsDefaultCopy && (
                    <DefaultItem>
                        <ShareButton
                            icon={<Link color="rgba(51, 51, 51, 0.5)" />}
                            text={<Translator tk="SHAREABLE_NOTIFICATION" />}
                            disabled
                        />
                    </DefaultItem>
                )}

                {needsCopyItem && (
                    <Item onClick={handleCopy}>
                        <CopyItem>
                            <Copy />
                            <Translator
                                tk={
                                    isActive
                                        ? 'REFERRALS_COPIED_NOTIFICATION'
                                        : 'REFERRALS_COPY_LINK'
                                }
                            />
                        </CopyItem>
                    </Item>
                )}

                {LinkList.filter((item) => isMobile || (!isMobile && !item.onlyForMobile)).map(
                    (button) => (
                        <Item key={button.text} onClick={handleClick(button.text, page)}>
                            <ShareButton
                                icon={button.icon}
                                text={button.text}
                                link={button.getLink(link, message)}
                            />
                        </Item>
                    ),
                )}
            </List>
        </Container>
    );
};

SocialSharingList.propTypes = {
    className: PropTypes.string,
    link: PropTypes.string,
    message: PropTypes.string,
    isMobile: PropTypes.bool.isRequired,
    needsTitle: PropTypes.bool,
    needsDefaultCopy: PropTypes.bool,
    needsCopyItem: PropTypes.bool,
    onClickItem: PropTypes.func,
    page: PropTypes.string,
    isReferralModal: PropTypes.bool,
};

SocialSharingList.defaultProps = {
    className: '',
    link: '',
    message: '',
    needsTitle: true,
    needsDefaultCopy: false,
    needsCopyItem: false,
    onClickItem: () => {},
    page: '',
    isReferralModal: false,
};

export default SocialSharingList;
